import { dataSchemaToTSDeclaration } from '@tencent/data-schema-core/lib/convertor/typescript';
import { getValidPascalCase } from '@tencent/data-schema-core/lib/convertor/typeOptions';
import { DBDatasourceMethods, FilterOperators, filterOperatorsMap, LogicOperator, } from './types/DBSource';
import { requestCGI } from './requestCGI';
import { DataSourceBase } from './DataSourceBase';
import { IDataSourceTypes } from './types/ICollection';
import { DATA_SOURCE_TYPE_PREFIX } from './utils/const';
import commonTSDeclaration from '!!raw-loader!./types/DBSource.d.ts';
const descByUIType = {
    100: 'Auto-increment ID, not required when creating',
    101: 'Custom ID, required when creating',
    102: 'System-generated ID, not required when creating',
    103: 'Custom ID, required when creating',
    104: 'Random ID, not required when creating',
};
export class DBSource extends DataSourceBase {
    static getCommonTSDeclaration() {
        return commonTSDeclaration;
    }
    filterSeparator = '__';
    async getTSDeclaration({ typePrefix = DATA_SOURCE_TYPE_PREFIX, source, withCommon = true, }) {
        const collection = source || await this.getCollectionInfo();
        const { entity } = collection;
        const entityTS = dataSchemaToTSDeclaration({ ...entity, id: entity?.id || this.collectionId, disableCache: true }, { noDsTitle: true });
        return [
            `type ${getValidPascalCase(`${typePrefix}-${this.collectionId}`)} = DBObject<${entityTS.exportedTypeId}>`,
            `${entityTS.getFullCode()}`,
            `${withCommon ? commonTSDeclaration : ''}`,
        ].join('\n');
    }
    async getExtraComments() {
        const collection = await this.getCollectionInfo();
        const { type, multiConfig, entity } = collection;
        const comments = [];
        // 多表数据源
        if (type === IDataSourceTypes.ER && multiConfig?.entities) {
            const relationships = this.processMultiTableRelationships(multiConfig.entities);
            comments.push(`## Multi-table Data Source
This data source is a multi-table data source, created by joining multiple related database tables. It supports ​**sub-table queries**, allowing you to find parent elements and their children by filtering on the child table's fields. You can also modify sub-table data by calling the data source's \`update\` method, which operates by having the new data completely overwrite the existing one. The relationships are below:
  ${relationships.map(item => `${item.from} to ${item.to} -> ${item.relationship}`)
                .join('\n')}
When constructing a query, you can employ the dot(\`.\`) to chain the names of related tables, forming a compound key for sub-table queries.

For example, if have a relationship like this:
  "foo to bar -> one-to-many, bar to baz -> one-to-one",
and foo is the main table, you can query like this:
\`\`\`typescript
const filter = {
  OR: [
    {'bar.name__eq': 'barName'},
    {'bar.baz.name__eq': 'bazName'},
  ]
};
\`\`\`
**Important**: A sub-table query filters the main table data based on sub-table fields; the sub-table data itself will not be filtered out. To remove non-matching entries from the sub-table results, manual post-processing is required.`);
            comments.push(`## Performance Considerations
Due to its multi-table nature, queries against this data source incur a higher performance cost, with a limit of 1000 (no longer 15000) records returned per query.
`);
        }
        // 一些特殊的字段定义，如主键
        comments.push(`## Special Field Definition​
${this.extractPrimaryKey(entity).map(item => `- ${item.path}: Primary Key${item.uiType ? `, ${descByUIType[item.uiType]}` : ''}`)
            .join('\n')}
`);
        return comments.join('\n');
    }
    async getMethods() {
        return {
            create: this.create.bind(this),
            update: this.update.bind(this),
            delete: this.delete.bind(this),
            getById: this.getById.bind(this),
            query: this.query.bind(this),
            count: this.count.bind(this),
        };
    }
    async create(data) {
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.create, {
            body: data,
        }, {});
        return this.handleResponse(response);
    }
    async update(id, data) {
        const { type } = await this.getCollectionInfo();
        // 多表数据源, 需要传入oldData
        if (type === IDataSourceTypes.ER) {
            const oldData = await this.getById(id);
            const response = await requestCGI(this.collectionId, DBDatasourceMethods.update, {
                query: { id },
                body: {
                    oldData,
                    // 接口要求传入所有字段
                    newData: {
                        ...oldData,
                        ...data,
                    },
                },
            }, {});
            return this.handleResponse(response);
        }
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.update, {
            query: { id },
            body: data,
        }, {});
        return this.handleResponse(response);
    }
    async delete(id) {
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.delete, {
            query: { id },
        }, {});
        return this.handleResponse(response);
    }
    async getById(id) {
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.get, {
            query: { id },
        }, {});
        return this.handleResponse(response);
    }
    async query(params) {
        const filter = this.parseFilter(params?.filter);
        const { sort, order } = this.parseSort(params?.sort);
        let defaultSize = 15000;
        const { type } = await this.getCollectionInfo();
        if (type === IDataSourceTypes.ER) {
            defaultSize = 1000;
        }
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.list, {
            query: {
                filter,
                sort,
                order,
                page: params?.page,
                size: params?.pageSize || defaultSize,
            },
        }, {});
        return this.handleResponse(response);
    }
    async count(params) {
        const filter = this.parseFilter(params?.filter);
        const response = await requestCGI(this.collectionId, DBDatasourceMethods.count, {
            query: { filter },
        }, {});
        return this.handleResponse(response).total;
    }
    handleResponse(response) {
        if (response.data.code !== 0) {
            throw new Error(response.data.message || `Unknown error:${response.data.code}`);
        }
        return response.data.data;
    }
    parseFilter(filter, parentOperator = '&') {
        if (!filter)
            return;
        const parts = [];
        Object.entries(filter).forEach(([key, value]) => {
            if ([LogicOperator.AND, LogicOperator.OR].includes(key)) {
                const logicOperator = key === LogicOperator.AND ? '&' : '|';
                const subQuery = value
                    .map(subFilter => `(${this.parseFilter(subFilter)})`)
                    .join(logicOperator);
                parts.push(subQuery);
            }
            else {
                const lastUnderscoreIndex = key.lastIndexOf(this.filterSeparator);
                let field = key;
                // 默认操作符为等于
                let operator = filterOperatorsMap[FilterOperators.eq];
                if (lastUnderscoreIndex !== -1) {
                    field = key.slice(0, lastUnderscoreIndex);
                    operator = filterOperatorsMap[key.slice(lastUnderscoreIndex + this.filterSeparator.length)];
                }
                if (!operator) {
                    throw new Error(`Unsupported filter operator: ${key}`);
                }
                parts.push(`${field}${operator}${JSON.stringify(value)}`);
            }
        });
        return parts.join(parentOperator);
    }
    parseSort(sortString) {
        if (!sortString)
            return {};
        const sort = [];
        const order = [];
        sortString.split(',').forEach((field) => {
            if (field.startsWith('-')) {
                sort.push(field.slice(1));
                order.push('desc');
            }
            else {
                sort.push(field);
                order.push('asc');
            }
        });
        return {
            sort: sort.join(','),
            order: order.join(','),
        };
    }
    // 找到多表数据源各表间的关系，用于prompt中的描述
    processMultiTableRelationships(entities) {
        // 1. 找到主实体
        const mainEntity = entities.find(entity => entity.main);
        if (!mainEntity)
            return [];
        const result = [];
        // 2. 递归遍历关系
        function traverseRelationships(currentEntity, currentRefId, isMainPath = true) {
            if (!currentEntity.relationships || currentEntity.relationships.length === 0) {
                return;
            }
            for (const rel of currentEntity.relationships) {
                const nextEntity = entities.find(e => e.entityId === rel.toEntityId);
                if (!nextEntity)
                    continue;
                // 创建关系对象
                const relationshipObj = {
                    from: currentRefId || currentEntity.entityId,
                    to: rel.refId || rel.toEntityId,
                    relationship: rel.type,
                    isMain: isMainPath,
                };
                result.push(relationshipObj);
                // 继续递归，后续关系不属于主路径
                traverseRelationships(nextEntity, rel.refId, false);
            }
        }
        // 3. 从主实体开始遍历
        traverseRelationships(mainEntity);
        return result;
    }
    extractPrimaryKey(entity) {
        // TODO: 联合主键目前没有标注，默认是无极的默认_id。如果要做可能需要请求表结构获取
        const primaryKeys = [];
        /**
         * 递归遍历字段，查找主键
         * @param {Array} fields - 字段数组
         * @param {string} currentPath - 当前路径
         */
        function traverseFields(fields, currentPath = '') {
            if (!Array.isArray(fields))
                return;
            for (const field of fields) {
                // 构建完整路径
                const fieldPath = currentPath ? `${currentPath}.${field.id}` : `${field.id}`;
                // 如果是主键，添加到结果中
                if (field.isPrimaryKey === true) {
                    primaryKeys.push({
                        field: `${field.id}`,
                        path: fieldPath,
                        type: field.type,
                        uiType: field.ui?.settings?.type || null,
                    });
                }
                // 递归处理嵌套字段
                if (field.type === 'object' && field.fields) {
                    traverseFields(field.fields, fieldPath);
                }
                // 处理数组类型的items中的对象字段
                if (field.type === 'array' && field.items) {
                    if (field.items.type === 'object' && field.items.fields) {
                        traverseFields(field.items.fields, fieldPath);
                    }
                }
            }
        }
        // 开始遍历
        if (entity.fields && Array.isArray(entity.fields)) {
            traverseFields(entity.fields);
        }
        return primaryKeys;
    }
}
